import { lookupTerm, lookupBatch, isTerminologyReady, normaliseSentence } from '../hooks/useTerminology';
import type { VeterinaryTerm } from '../worker';
import { COMMON_WORDS, isCommonWord, isLikelyVeterinaryTerm } from '../constants/commonWords';

// Fallback for when worker is not ready
const BASIC_FUZZY_PATTERNS: Record<string, string> = {
  'ban a mine': 'Banamine',
  'bana mine': 'Banamine',
  'but': 'Bute',
  'butt': 'Bute',
  'ace': 'Acepromazine',
  'dex': 'Dexamethasone',
  'dexa': 'Dexamethasone',
  'genta': 'Gentamicin',
  'pen': 'Penicillin',
  'founder': 'Laminitis',
  'nav': 'Navicular',
  'tying up': 'Azoturia',
  'monday morning': 'Azoturia',
  'x ray': 'Radiographs',
  'x-ray': 'Radiographs',
  'xray': 'Radiographs'
};

/**
 * Find veterinary term with fallback logic
 */
async function findVeterinaryTermWithFallback(word: string): Promise<string> {
  // Try worker first if ready
  if (isTerminologyReady()) {
    const term = await lookupTerm(word);
    if (term) {
      return term.term;
    }
  }
  
  // Fallback to basic fuzzy patterns
  const lowercaseWord = word.toLowerCase();
  return BASIC_FUZZY_PATTERNS[lowercaseWord] || word;
}

/**
 * Corrects veterinary terminology in transcribed text
 */
export async function correctVeterinaryTerms(text: string): Promise<{
  correctedText: string;
  corrections: Array<{ original: string; corrected: string; position: number }>;
}> {
  const corrections: Array<{ original: string; corrected: string; position: number }> = [];
  let correctedText = text;
  
  // Split text into words while preserving punctuation and spacing
  const words = text.split(/(\s+|[.,;:!?])/);
  let currentPosition = 0;
  
  // Collect all words that need lookup
  const wordsToLookup: { index: number; word: string; cleanWord: string; position: number }[] = [];
  
  for (let i = 0; i < words.length; i++) {
    const word = words[i];
    const cleanWord = word.replace(/[.,;:!?]/g, '').toLowerCase();

    // Skip if it is a common word regardless of punctuation/case
    if (isCommonWord(cleanWord)) {
      currentPosition += word.length;
      continue;
    }
    
    const shouldCheck = cleanWord.length >= 3 && (
      isTerminologyReady() ? true : isLikelyVeterinaryTerm(cleanWord)
    );

    if (shouldCheck) {
      wordsToLookup.push({ index: i, word, cleanWord, position: currentPosition });
    }
    
    currentPosition += word.length;
  }
  
  // Batch lookup if worker is ready, otherwise use fallback
  if (isTerminologyReady() && wordsToLookup.length > 0) {
    const lookupWords = wordsToLookup.map(w => w.cleanWord);
    const results = await lookupBatch(lookupWords);
    
    for (let i = 0; i < wordsToLookup.length; i++) {
      const { index, word, cleanWord, position } = wordsToLookup[i];
      const result = results[i];
      
      if (result?.term) {
        const canonical = result.term.term;
        
        // Skip if no actual change needed
        if (canonical.toLowerCase() === word.toLowerCase()) {
          continue;
        }

        const finalCorrection = preserveCase(word, canonical);
        
        corrections.push({
          original: word,
          corrected: finalCorrection,
          position
        });
        
        // Replace in the corrected text
        correctedText = correctedText.replace(word, finalCorrection);
        words[index] = finalCorrection;
      }
    }
  } else {
    // Fallback to individual lookups with basic patterns
    for (const { index, word, cleanWord, position } of wordsToLookup) {
      const correctedTerm = await findVeterinaryTermWithFallback(cleanWord);
      
      if (correctedTerm !== cleanWord && correctedTerm !== word && correctedTerm.toLowerCase() !== cleanWord) {
        const finalCorrection = preserveCase(word, correctedTerm);
        
        corrections.push({
          original: word,
          corrected: finalCorrection,
          position
        });
        
        // Replace in the corrected text
        correctedText = correctedText.replace(word, finalCorrection);
        words[index] = finalCorrection;
      }
    }
  }
  
  return { correctedText, corrections };
}


/**
 * Preserves the case pattern of the original word when applying corrections
 */
function preserveCase(original: string, correction: string): string {
  if (original === original.toLowerCase()) {
    return correction.toLowerCase();
  }
  if (original === original.toUpperCase()) {
    return correction.toUpperCase();
  }
  if (original[0] === original[0].toUpperCase()) {
    return correction.charAt(0).toUpperCase() + correction.slice(1).toLowerCase();
  }
  return correction;
}

/**
 * Enhanced correction that handles multi-word veterinary terms
 */
export async function correctVeterinaryTermsAdvanced(text: string): Promise<{
  correctedText: string;
  corrections: Array<{ original: string; corrected: string; position: number }>;
}> {
  const corrections: Array<{ original: string; corrected: string; position: number }> = [];

  // First, ask the worker to normalise multi-word units like "milligrams per kilogram" → "mg/kg"
  let correctedText = isTerminologyReady() ? await normaliseSentence(text) : text;

  // Record a top-level correction if the worker changed anything
  if (correctedText !== text) {
    corrections.push({
      original: text,
      corrected: correctedText,
      position: 0,
    });
  }
  
  // Then handle single-word terms
  const singleWordResult = await correctVeterinaryTerms(correctedText);
  correctedText = singleWordResult.correctedText;
  corrections.push(...singleWordResult.corrections);
  
  return { correctedText, corrections };
}

/**
 * Handles multi-word veterinary terms that might be misrecognized
 */
function handleMultiWordTerms(
  text: string, 
  corrections: Array<{ original: string; corrected: string; position: number }>
): string {
  let correctedText = text;
  
  // Common multi-word terms that might be misrecognized
  const multiWordPatterns: Array<{ pattern: RegExp; replacement: string }> = [
    { pattern: /ban\s*a\s*mine/gi, replacement: 'Banamine' },
    { pattern: /dormo\s*sed[ae]n/gi, replacement: 'Dormosedan' },
    { pattern: /ace\s*pro\s*ma?z?i?n?e?/gi, replacement: 'Acepromazine' },
    { pattern: /dexa?\s*meth\s*a?\s*sone?/gi, replacement: 'Dexamethasone' },
    { pattern: /o\s*mep\s*ra\s*zole?/gi, replacement: 'Omeprazole' },
    { pattern: /gastro\s*gard/gi, replacement: 'Gastrogard' },
    { pattern: /ulcer\s*gard/gi, replacement: 'Ulcergard' },
    { pattern: /lam\s*in\s*i?t?i?s/gi, replacement: 'Laminitis' },
    { pattern: /nav\s*ic\s*u?\s*lar/gi, replacement: 'Navicular' },
    { pattern: /strep\s*equi?/gi, replacement: 'Strangles' },
    { pattern: /tying\s*up/gi, replacement: 'Azoturia' },
    { pattern: /monday\s*morning\s*disease/gi, replacement: 'Azoturia' },
    { pattern: /gastro\s*scopy/gi, replacement: 'Gastroscopy' },
    { pattern: /arthro\s*scopy/gi, replacement: 'Arthroscopy' },
    { pattern: /radio\s*graphs?/gi, replacement: 'Radiographs' },
    { pattern: /x[\s-]*rays?/gi, replacement: 'Radiographs' },
    { pattern: /ultra\s*sounds?/gi, replacement: 'Ultrasound' },
    { pattern: /naso\s*gastric/gi, replacement: 'Nasogastric' },
    { pattern: /ng\s*tube/gi, replacement: 'Nasogastric tube' },
    { pattern: /fet\s*lock/gi, replacement: 'Fetlock' },
    { pattern: /pas\s*tern/gi, replacement: 'Pastern' },
    { pattern: /with\s+ers/gi, replacement: 'Withers' },
    { pattern: /sti\s*fle/gi, replacement: 'Stifle' }
  ];
  
  multiWordPatterns.forEach(({ pattern, replacement }) => {
    const matches = correctedText.match(pattern);
    if (matches) {
      matches.forEach(match => {
        const position = correctedText.indexOf(match);
        corrections.push({
          original: match,
          corrected: replacement,
          position
        });
      });
      correctedText = correctedText.replace(pattern, replacement);
    }
  });
  
  return correctedText;
}

/**
 * Real-time correction as the user types or speaks
 */
export async function correctTextRealTime(text: string, cursorPosition: number = text.length): Promise<{
  correctedText: string;
  newCursorPosition: number;
  wasCorrection: boolean;
}> {
  const result = await correctVeterinaryTermsAdvanced(text);
  
  if (result.corrections.length === 0) {
    return {
      correctedText: text,
      newCursorPosition: cursorPosition,
      wasCorrection: false
    };
  }
  
  // Adjust cursor position based on corrections
  let newCursorPosition = cursorPosition;
  result.corrections.forEach(correction => {
    if (correction.position < cursorPosition) {
      const lengthDiff = correction.corrected.length - correction.original.length;
      newCursorPosition += lengthDiff;
    }
  });
  
  return {
    correctedText: result.correctedText,
    newCursorPosition,
    wasCorrection: true
  };
}

/**
 * Get suggestions for partial veterinary terms
 */
export async function getVeterinaryTermSuggestions(partialTerm: string, maxSuggestions: number = 5): Promise<string[]> {
  const lowercasePartial = partialTerm.toLowerCase();
  const suggestions: string[] = [];
  
  // For now, return empty array as terms are in worker
  // This could be enhanced to request suggestions from worker
  // or cache frequently used terms in main thread
  
  // Basic fallback suggestions from fuzzy patterns
  Object.entries(BASIC_FUZZY_PATTERNS).forEach(([pattern, term]) => {
    if (pattern.includes(lowercasePartial) || term.toLowerCase().includes(lowercasePartial)) {
      if (!suggestions.includes(term)) {
        suggestions.push(term);
      }
    }
  });
  
  return suggestions.slice(0, maxSuggestions);
}
